import React, { useCallback, useContext, useState } from 'react'
import axiosUtils, { MUTATION_VERBS_HEADER } from 'utils/api/axiosUtils'
import axios from 'utils/api/axios'
import { Message, MessageSeverity } from 'constants/message'
import i18n from '../../i18n'
import MessageContext from 'context/MessageContext'
import AuthContext from 'context/AuthContext'
import LoadingContext from 'context/LoadingContext'
import { HttpStatusCode } from 'axios'

function getSuccessMessageKey(method: keyof typeof MUTATION_VERBS_HEADER) {
  let msgKey = null
  switch (method) {
    case 'POST':
      msgKey = '7009204'
      break
    case 'PUT':
      msgKey = 'updateSuccessful'
      break
    case 'DELETE':
      msgKey = '3C00002'
      break
    default:
      throw new Error('Unknown method')
  }

  return msgKey
}

function useMutation<T>(url: string, nameCaller: string = '-', deps: React.DependencyList = []) {
  const [error, setError] = useState<Error | null>(null)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { setIsConnected, handleRequestLogin, setModalLogInOpened } = useContext(AuthContext)
  const { isLoading, handleLoadInProgress } = useContext(LoadingContext)

  const mutate = useCallback(
    async ({
      data,
      params,
      method,
    }: {
      data?: Record<string, unknown>
      params?: Record<string, string>
      method: keyof typeof MUTATION_VERBS_HEADER
    }) => {
      handleLoadInProgress(true)
      setError(null)
      try {
        const headerCompleted = await axiosUtils.getHeader(MUTATION_VERBS_HEADER[method])
        console.debug(
          'Component <' +
            nameCaller +
            '> :: called URL = ' +
            encodeURI(url) +
            '[params: ' +
            JSON.stringify(params) +
            ']',
        )
        const response = await axios.request<T>({
          method,
          url: encodeURI(url),
          data,
          params,
          headers: headerCompleted,
          withCredentials: false,
        })

        console.debug('..component <' + nameCaller + '> :: data retrieved')
        handleDisplayMessage(new Message(MessageSeverity.SUCCESS, '', null, i18n.t(getSuccessMessageKey(method))))
        return response
      } catch (err) {
        console.debug('ERR with component <' + nameCaller + '>: ' + err)

        setError(err instanceof Error ? err : new Error(String(err)))

        if (err.response.status === HttpStatusCode.Unauthorized) {
          handleDisplayMessage(new Message(MessageSeverity.WARNING, '', null, i18n.t('sessionExpired')))
          setIsConnected(false)
          setModalLogInOpened(true)
          handleRequestLogin()
        } else if (err.response?.status === HttpStatusCode.Forbidden) {
          handleDisplayMessage(new Message(MessageSeverity.ERROR, '', null, i18n.t('9500138')))
        } else if (err.response.status === HttpStatusCode.NotFound) {
          handleDisplayMessage(new Message(MessageSeverity.ERROR, '', null, i18n.t('9500173')))
        } else {
          const message = err.response.data.hasOwnProperty('messages')
            ? err.response.data.messages[0]
            : err.response.data
          handleDisplayMessage(
            new Message(
              MessageSeverity.ERROR,
              '',
              message?.amasisCode,
              message?.title ||
                message?.message ||
                message?.description ||
                message?.error ||
                i18n.t(message?.amasisCode),
            ),
          )
        }

        throw err
      } finally {
        handleLoadInProgress(false)
      }
    },
    deps,
  )

  return { isLoading: isLoading, error, mutate }
}

export default useMutation
